import React, { useContext, useState } from 'react';
import {
  useHistory,
  useLocation,
} from 'react-router-dom/cjs/react-router-dom.min';
import ExitHub from '../../static/img/exithubgray26.svg';
import { getUserRole } from '../../userLocalDetails.js';
import { isShowExitHub } from '../../components/MainSidebar/rules';
import { buildNavItems } from './navItems';
import {
  Logo,
  SideNavbar,
  SidebarContainer,
  SideBottomContainer,
  SideBottomItem,
} from './style';
import TourTooltip from '../../components2/TourTooltip';
import ExclamationIcon from '../../static/img/report-issue.svg';
import ReportIssuePopup from '../../components/ReportIssuePopup';
import lockIcon from '../../static/icons/lock.svg';
import SchoolPlanModal from '../../components2/freemium/SchoolPlanModal';
import { AppContext } from '../../app.context';
import { useTour } from '../../TourContext.jsx';
import { useProfile } from '../../components/state/hooks';
import CircularProgressWithLabel from '../../components2/TeacherOnboarding/CircularProgress.jsx';
import { STEP_TEXT, TIP_COUNTS } from '../tours/tourCopy';
import { goToStudyGuides } from '../../service';

const studyGuideItem = {
  icon: ExitHub,
  activeIcon: ExitHub,
  name: 'Study Guides',
  isActive: false,
  link: '#',
  // During TOURS the mirror sidebar swallows the Study Guides click to keep
  // the user on the sandbox. While the setup CHECKLIST is open (no tour),
  // handlePageRoute opens jeddleBaseUrl/my-courses in a new tab instead —
  // same as production — since a new tab never leaves the setup screen.
  action: () => {},
  hasSeparator: true,
  isStudyGuide: true,
};

const MainSidebar = ({ isSchoolTeacherProfile }) => {
  const history = useHistory();
  const location = useLocation();
  const markingToolsRef = React.useRef(null);
  const draftsRef = React.useRef(null);
  const { isSidebarOverlayActive } = useContext(AppContext) || {};
  const {
    markingTourActive,
    setMarkingTourActive,
    markingTourStep,
    setMarkingTourStep,
    draftTourActive,
    setDraftTourActive,
    draftTourStep,
    setDraftTourStep,
    showOnboardingComplete,
    setShowOnboardingComplete,
    setShowDraftFeedback,
    onboardingTasks,
    setOnboardingTasks,
    setNewTaskTourActive,
    feedbackTourActive,
    newTaskTourActive,
    newTaskTourStep,
    wizardTourActive,
    onboardingProgress,
    requestTourExit,
  } = useTour();

  const [showReportPopup, setShowReportPopup] = useState(false);
  const [isSchoolPlanModalOpen, setIsSchoolPlanModalOpen] = useState(false);
  // What the locked item was, for the school-plan popup (as the production
  // sidebar passes it), so its enquiry names the feature reached for.
  const [lockedFeature, setLockedFeature] = useState('');

  const { data: profile } = useProfile();
  // The nav mirrors the production sidebar's permission gates (Tasks / Class
  // Insights shown unless HIDDEN, locked when LOCKED), so the demo nav matches
  // the user's real one (JED-1101, JED-1107). App.jsx renders a <Loader/> until
  // profile has loaded and SidebarSwitcher only mounts this mirror afterwards,
  // so permissions are populated by the time this runs.
  const permissions = profile?.permissions;

  const userRole = getUserRole();
  const showSetupProgressItem = isShowExitHub() && isSchoolTeacherProfile;
  const sideNavItems = (() => {
    const items = buildNavItems(
      location,
      userRole,
      permissions,
      showOnboardingComplete
    );
    if (isShowExitHub()) items.push(studyGuideItem);
    return items;
  })();

  // Tour-aware re-entry handler for the Setup Progress ring. Same intent
  // as the production sidebar (reopen the checklist), but the mirror has to
  // tear down any in-flight tour and bounce the user out of /onboarding/*
  // first — otherwise TourGuard's history.listen would intercept the
  // history.push('/tasks') with TourExitWarning and the item would feel
  // like a dead-end.
  const handleSetupProgressClick = () => {
    setShowOnboardingComplete(true);
    setDraftTourActive(false);
    setMarkingTourActive(false);
    setShowDraftFeedback(false);
    setNewTaskTourActive(false);
    history.push('/tasks');
  };

  const updateTaskCompletion = (index, isCompleted = true) => {
    if (!onboardingTasks) return;
    const next = [...onboardingTasks];
    next[index] = isCompleted;
    setOnboardingTasks(next);
  };

  const handlePageRoute = (navItem) => {
    // While the setup checklist is the visible screen (no tour running —
    // during tours the whole sidebar is click-locked via
    // body[data-tour-active]), EVERY other navbar destination must be
    // confirmed via TourExitWarning: TourGuard owns the dialog; confirm
    // tears onboarding down (persisting the dismissal) and navigates,
    // cancel stays on the checklist. This replaces the old JED-1112
    // silent sanctioned exit for Tasks / Class Insights — leaving setup
    // is now always an explicit, warned choice. Study Guides is the one
    // exception: it opens a NEW TAB, so this tab never leaves setup.
    const anyTourActive =
      markingTourActive ||
      draftTourActive ||
      newTaskTourActive ||
      feedbackTourActive;
    if (showOnboardingComplete && !anyTourActive) {
      if (navItem.isStudyGuide) {
        goToStudyGuides();
        return;
      }
      if (typeof requestTourExit === 'function') {
        requestTourExit(navItem.link);
        return;
      }
    }

    if (navItem.locked) {
      setLockedFeature(navItem.name || '');
      setIsSchoolPlanModalOpen(true);
      return;
    }

    if (navItem.action) {
      navItem.action();
      return;
    }

    // Otherwise plain navigation. The sidebar is never the entry point for a
    // tour; only OnboardingComplete row clicks set tour state. If a tour is
    // already active when the user clicks the sidebar, TourGuard will bounce
    // them back inside /onboarding/*.
    if (history.location.pathname !== navItem.link) {
      history.push(navItem.link);
    }
  };

  return (
    <SidebarContainer
      isBlurred={isSidebarOverlayActive && !feedbackTourActive}
      showOnTop={
        (markingTourActive && markingTourStep === 1) ||
        (draftTourActive && draftTourStep === 1)
      }
      isDimmed={
        feedbackTourActive ||
        markingTourActive ||
        draftTourActive ||
        // Not on new-task tip 3: its in-place frame's spread shadow already
        // dims the whole page, this sidebar included, so a second dim read
        // darker there than on every other tip (JED-1273). The
        // body[data-tour-active] click-lock still blocks the sidebar.
        (newTaskTourActive && newTaskTourStep !== 3) ||
        // Wizard tour (non-school audiences): TourGuard already click-locks
        // the sidebar via body[data-tour-active]; without the dim it looked
        // fully live but silently ate every click.
        wizardTourActive
      }
      isSetupCompleteOpen={showOnboardingComplete}
    >
      <SideNavbar>
        {/*
          Logo used to be `<a href="/">`, which did a hard page load and bypassed
          TourGuard. Now uses an onClick handler so TourGuard can intercept.
        */}
        <span
          role="link"
          tabIndex={0}
          onClick={() => history.push('/')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') history.push('/');
          }}
          style={{ cursor: 'pointer' }}
        >
          <Logo src="./img/logo.svg" />
        </span>
        <ul>
          {sideNavItems.map((navItem, index) => {
            const active = navItem.isActive;
            const showActiveBackground = active && navItem.needsActiveBackground;
            const iconSize = navItem.iconSize || 48;
            const isMarkingTools = navItem.name === 'Marking Tools';
            // Spotlighted step-1 items (raised above the dim ::after at
            // zIndex 10002): the tooltip tells the user to CLICK the item it
            // points at, so clicking it must advance the tour exactly like
            // the tooltip's Next. The demo-draft guard matches the draft
            // step-1 tooltip's own render gate below — draft step 1 on any
            // other /onboarding/documents/* path stays inert. Locked items
            // keep the plan-modal path in handlePageRoute.
            const isMarkingSpotlight =
              markingTourActive && markingTourStep === 1 && isMarkingTools;
            const isDraftSpotlight =
              draftTourActive &&
              draftTourStep === 1 &&
              navItem.name === 'My Drafts' &&
              location.pathname.includes('demo-draft');
            const isSpotlighted =
              !navItem.locked && (isMarkingSpotlight || isDraftSpotlight);
            return (
              <React.Fragment key={navItem.link || index}>
                {navItem.hasSeparator && <hr style={{ width: '100%', margin: '5px 0' }} />}
                <li
                  className={`${navItem.locked ? 'locked' : ''} ${navItem.isStudyGuide ? 'study-guide' : ''}${showActiveBackground ? ' active-bg' : ''}`}
                  // globals.css restores pointer-events on data-tour-target
                  // descendants while body[data-tour-active] click-locks the
                  // rest of the app container.
                  data-tour-target={isSpotlighted ? 'true' : undefined}
                  onClick={() => {
                    // Absolute (not prev+1) step set: idempotent under a
                    // double click; step 1 is never the last step so none of
                    // TourTooltip's completion side-effects apply.
                    if (isSpotlighted && isMarkingSpotlight) {
                      setMarkingTourStep(2);
                      return;
                    }
                    if (isSpotlighted && isDraftSpotlight) {
                      // Mirror the draft step-1 tooltip's Next (below):
                      // customise popup + advance.
                      setShowDraftFeedback(true);
                      setDraftTourStep(2);
                      return;
                    }
                    handlePageRoute(navItem);
                  }}
                  ref={
                    isMarkingTools
                      ? markingToolsRef
                      : navItem.name === 'My Drafts'
                      ? draftsRef
                      : null
                  }
                  style={
                    isMarkingSpotlight || isDraftSpotlight
                      ? {
                          position: 'relative',
                          zIndex: 10002,
                          padding: '8px 12px',
                          boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                          // Only advertise clickability when the click-to-
                          // advance handler is armed (locked items keep the
                          // spotlight look but stay inert under the tour lock).
                          ...(isSpotlighted ? { cursor: 'pointer' } : {}),
                        }
                      : {}
                  }
                >
                  <div className={`icon-wrapper${showActiveBackground ? ' active-bg' : ''}`}>
                    <img
                      src={active ? navItem.activeIcon : navItem.icon}
                      // The inline cursor would otherwise override the
                      // li-level pointer over the biggest hit area — visible
                      // now that the alias fix makes My Drafts ACTIVE on the
                      // demo-draft route while it is also the spotlight.
                      style={{
                        cursor: active && !isSpotlighted ? 'auto' : 'pointer',
                        width: iconSize,
                        height: iconSize,
                      }}
                    />
                    {navItem.locked && <img className="lock-badge" src={lockIcon} />}
                  </div>
                  <span className={active ? 'active' : ''}>{navItem.name}</span>

                  {isMarkingTools && markingTourActive && markingTourStep === 1 && (
                    <TourTooltip
                      text={STEP_TEXT.marking[1]}
                      currentStep={markingTourStep}
                      tipCount={TIP_COUNTS.marking}
                      reference={markingToolsRef}
                      position="right"
                    />
                  )}

                  {draftTourActive &&
                    draftTourStep === 1 &&
                    navItem.name === 'My Drafts' &&
                    location.pathname.includes('demo-draft') && (
                      <TourTooltip
                        text={STEP_TEXT.draft[1]}
                        currentStep={draftTourStep}
                        tipCount={TIP_COUNTS.draft}
                        onNext={() => {
                          setShowDraftFeedback(true);
                        }}
                        reference={draftsRef}
                        position="right"
                      />
                    )}
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      </SideNavbar>
      {isShowExitHub() && (
        <SideBottomContainer>
          {showSetupProgressItem && (
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
              <CircularProgressWithLabel
                value={onboardingProgress}
                onClick={handleSetupProgressClick}
              />
            </div>
          )}
          <SideBottomItem onClick={() => setShowReportPopup(true)}>
            <img src={ExclamationIcon} />
            <p>Report Issue</p>
          </SideBottomItem>
        </SideBottomContainer>
      )}
      {showReportPopup && <ReportIssuePopup onClose={() => setShowReportPopup(false)} />}
      <SchoolPlanModal
        isOpen={isSchoolPlanModalOpen}
        onClose={() => setIsSchoolPlanModalOpen(false)}
        feature={lockedFeature}
      />
    </SidebarContainer>
  );
};

export default MainSidebar;
